// ============================================================
//  CREW STORE — Zustand + Firebase Realtime Sync
// ============================================================

import { create } from "zustand";
import { ref, set as fbSet, remove, onValue, off } from "firebase/database";
import { db } from "../firebase/config";
import { generateId, formatWorkDate } from "../utils";
import type { Crew, Delivery, FilterType } from "../types";

const LOCAL_KEY = "fleetops_crews";
const DATE_KEY = "fleetops_viewing_date"; // persist viewingDate across refreshes

function loadLocal(): Crew[] {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_KEY) || "[]");
  } catch {
    return [];
  }
}
function saveLocal(crews: Crew[]) {
  localStorage.setItem(LOCAL_KEY, JSON.stringify(crews));
}

// Persist + restore viewingDate so refresh never loses the date
function loadViewingDate(): Date {
  try {
    const iso = localStorage.getItem(DATE_KEY);
    if (iso) {
      const d = new Date(iso);
      if (!isNaN(d.getTime())) return d;
    }
  } catch {}
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}
function saveViewingDate(d: Date) {
  localStorage.setItem(DATE_KEY, d.toISOString());
}

// ---- Firebase helpers ----
function pushCrew(c: Crew) {
  fbSet(ref(db, `crews/${c.id}`), c).catch((e) =>
    console.warn("[crews] push failed", e),
  );
}
function updateCrew(c: Crew) {
  fbSet(ref(db, `crews/${c.id}`), c).catch((e) =>
    console.warn("[crews] update failed", e),
  );
}
function removeCrew(id: string) {
  remove(ref(db, `crews/${id}`)).catch((e) =>
    console.warn("[crews] remove failed", e),
  );
}

interface CrewState {
  crews: Crew[];
  viewingDate: Date;
  searchQuery: string;
  activeFilter: FilterType;
  currentCrewId: string | null;

  setViewingDate: (d: Date) => void;
  setSearchQuery: (q: string) => void;
  setActiveFilter: (f: FilterType) => void;
  setCurrentCrewId: (id: string | null) => void;

  addCrew: (crew: Crew) => void;
  updateCrew: (id: string, updater: (c: Crew) => Crew) => void;
  removeCrew: (id: string) => void;
  getById: (id: string) => Crew | undefined;

  startSync: () => () => void;
}

export const useCrewStore = create<CrewState>((set, get) => ({
  crews: loadLocal(),
  viewingDate: loadViewingDate(), // ← persisted, survives refresh
  searchQuery: "",
  activeFilter: "all",
  currentCrewId: null,

  setViewingDate(d) {
    saveViewingDate(d); // ← persist every time it changes
    set({ viewingDate: d });
  },
  setSearchQuery: (q) => set({ searchQuery: q }),
  setActiveFilter: (f) => set({ activeFilter: f }),
  setCurrentCrewId: (id) => set({ currentCrewId: id }),

  addCrew(crew) {
    const next = [crew, ...get().crews];
    saveLocal(next);
    set({ crews: next });
    pushCrew(crew);
  },

  updateCrew(id, updater) {
    const crews = get().crews.map((c) =>
      c.id === id ? updater(JSON.parse(JSON.stringify(c))) : c,
    );
    saveLocal(crews);
    set({ crews });
    const updated = crews.find((c) => c.id === id);
    if (updated) updateCrew(updated);
  },

  removeCrew(id) {
    const crews = get().crews.filter((c) => c.id !== id);
    saveLocal(crews);
    set({ crews });
    removeCrew(id);
  },

  getById(id) {
    return get().crews.find((c) => c.id === id);
  },

  startSync() {
    const crewsRef = ref(db, "crews");
    const unsub = onValue(crewsRef, (snap) => {
      const raw = snap.val();
      // Always sync from Firebase — this is the single source of truth
      // When raw is null (all crews deleted), wipe local state too
      const crews: Crew[] = raw ? Object.values(raw) : [];
      saveLocal(crews);
      set({ crews });
    });
    return () => unsub();
  },
}));

// ---- Delivery helpers ----
export function makeDelivery(address: string): Delivery {
  return {
    id: generateId(),
    address,
    delivered: false,
    assembled: false,
    completedAt: null,
    addedAt: new Date().toISOString(),
  };
}

export function makeCrew(
  driver: string,
  driverDob: string,
  sideman: string,
  sidemanDob: string,
  dateVal: string,
  startTime?: string,
  routeNumber?: string,
  photo?: string | null,
  sideman2Name?: string,
  sideman2Dob?: string,
  driverProfileId?: string,
  sidemanProfileId?: string,
  sideman2ProfileId?: string,
): Crew {
  return {
    id: generateId(),
    createdAt: new Date().toISOString(),
    workDate: formatWorkDate(dateVal),
    startTime: startTime || null,
    routeNumber: routeNumber || null,
    photo: photo || null,
    driver: {
      name: driver,
      dob: driverDob,
      profileId: driverProfileId || null,
      confirmed: false,
      confirmedAt: null,
    },
    sideman: {
      name: sideman,
      dob: sidemanDob,
      profileId: sidemanProfileId || null,
      confirmed: false,
      confirmedAt: null,
    },
    sideman2: sideman2Name
      ? {
          name: sideman2Name,
          dob: sideman2Dob || "",
          profileId: sideman2ProfileId || null,
          confirmed: false,
          confirmedAt: null,
        }
      : null,
    deliveries: [],
  };
}
